import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatWeight } from '@vj/shared';
import { api } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { VoucherTabs } from './VoucherTabs.jsx';

const STATUS_TONE = {
  pending: 'text-muted',
  partially_returned: 'text-warning',
  fully_returned: 'text-success',
  cancelled: 'text-danger',
};

const STATUS_LABEL = {
  pending: 'Pending',
  partially_returned: 'Partially returned',
  fully_returned: 'Fully returned',
  cancelled: 'Cancelled',
};

const COLUMNS = [
  { key: 'voucher_no', header: 'Voucher #', sortKey: 'voucher_no' },
  { key: 'issue_date', header: 'Date', sortKey: 'issue_date', render: (r) => new Date(r.issue_date).toLocaleDateString('en-IN') },
  { key: 'issued_to_name', header: 'Issued to' },
  {
    key: 'expected_return_date',
    header: 'Expected back',
    sortKey: 'expected_return_date',
    render: (r) => {
      if (!r.expected_return_date) return '—';
      const overdue = !['fully_returned', 'cancelled'].includes(r.status) && new Date(r.expected_return_date) < new Date(new Date().toDateString());
      return <span className={overdue ? 'text-danger' : ''}>{new Date(r.expected_return_date).toLocaleDateString('en-IN')}</span>;
    },
  },
  { key: 'total_net_weight', header: 'Net weight', align: 'right', render: (r) => formatWeight(r.total_net_weight) },
  { key: 'status', header: 'Status', render: (r) => <span className={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</span> },
];

export default function IssuesListPage() {
  const [query, onQueryChange] = useListQuery({ sort: 'issue_date' });
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const navigate = useNavigate();

  const listQuery = useQuery({
    queryKey: ['issues', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/issues', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Issue &amp; receipt vouchers</h1>
        <Button onClick={() => navigate('/issue-receipt/new')}>
          <Plus size={17} aria-hidden="true" /> New issue
        </Button>
      </div>

      <VoucherTabs />

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        rowKey={(row) => row.issue_id}
        onRowClick={(row) => navigate(`/issue-receipt/${row.issue_id}`)}
        searchPlaceholder="Search by voucher number or name…"
        emptyLabel="No issue vouchers yet."
      />
    </div>
  );
}
